import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { clientEvents } from "../../../api/client/listing";
import EventDetailsModal from "./EventDetailsModal";

const Past = () => {
  const navigate = useNavigate();
  const [pastBookings, setPastBookings] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [loadingState, setLoadingState] = useState("initial");
  const [showIncomeDetails, setShowDetails] = useState({
    status: false,
    code: "",
    serviceOffered: {},
    isRecommend: false,
  });

  const fetchEvents = async (page = 1, refreshing = false) => {
    const requestData = {
      flag: "past_events",
      page_no: refreshing ? 1 : page,
      row_per_page: 10,
    };

    try {
      setLoadingState(
        refreshing ? null : page === 1 ? "initial" : "pagination"
      );
      const response = await clientEvents(requestData);
      if (response.error === 0) {
        const { past_bookings, pagination } = response.data;
        setPastBookings((prev) =>
          page === 1 ? past_bookings : [...prev, ...past_bookings]
        );
        setCurrentPage(page + 1);
        setIsLastPage(pagination.current_page === pagination.total_page);
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoadingState(null);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEvents(); // Initial fetch
  }, []);

  useEffect(() => {
    if (!isLastPage && !loadingState) {
      handleLoadMore();
    }
  }, [isLastPage, loadingState]);

  const handleRefresh = useCallback(() => {
    if (!isRefreshing) {
      setPastBookings([]);
      setIsLastPage(false);
      setCurrentPage(1);
      setIsRefreshing(true);
      fetchEvents(1, true);
    }
  }, [isRefreshing]);

  const handleLoadMore = () => {
    if (!loadingState && !isLastPage) {
      fetchEvents(currentPage);
    }
  };

  const renderEmpty = () => (
    <div className="flex mt-24 flex-col items-center px-8 space-y-2">
      <div className="items-center w-24 h-24 mr-3">
        <div className="no-item h-full w-full" />
      </div>
      <div className="text-center font-medium text-gray-700 text-lg">
        No event yet. Where’s the fun in that?!
      </div>
      <div className="text-center text-sm text-gray-500">
        You haven’t booked any service yet. Let’s start a celebration and make
        reservations now!
      </div>
    </div>
  );

  const renderItem = useCallback(
    (item) => {
      const { container, text } = statusHandlerColor(item?.booking_status);
      return (
        <div className="mb-4 p-4 border border-gray-300 rounded-lg">
          <button
            className="w-full justify-items-start"
            onClick={() => {
              if (item?.is_recommended !== "0") {
                setShowDetails({
                  status: true,
                  code: item.booking_code,
                  serviceOffered: item,
                  isRecommend: false,
                });
              }
            }}
          >
            <div className="flex flex-col sm:flex-row space-x-0 sm:space-x-4">
              <img
                src={item.cover_img}
                className="h-24 w-24 rounded-xl object-cover mb-4 sm:mb-0"
              />
              <div className="flex-1 space-y-1">
                <div className=" font-semibold">{item?.title}</div>
                <div className={`items-center ${container}`}>
                  <div className={text}>
                    {statusHandler(item?.booking_status)}
                  </div>
                </div>

                <div className="flex flex-col text-primary-600 text-xs mt-3">
                  <div className="flex gap-x-2 items-center flex-row">
                    <div className="w-5 h-5">
                      <div
                        className="icon-calendar h-full w-full "
                        style={{ backgroundPositionY: "center" }}
                      />
                    </div>
                    <p>{item.event_date}</p>
                  </div>
                  <div className="flex gap-x-2 items-center flex-row">
                    <div className="w-5 h-5">
                      <div
                        className="icon-location h-full w-full "
                        style={{ backgroundPositionY: "center" }}
                      />
                    </div>
                    <p>{item.location}</p>
                  </div>
                </div>
              </div>
            </div>
          </button>
          {item?.is_recommended === "0" ? (
            <button
              className="mt-4 p-2 w-full rounded-full border border-primary-500 bg-white text-primary-500 font-semibold"
              onClick={() => {
                setShowDetails({
                  status: true,
                  code: item.booking_code,
                  serviceOffered: item,
                  isRecommend: true,
                });
              }}
            >
              <div className="text-sm text-center">Recommend</div>
            </button>
          ) : null}
        </div>
      );
    },
    [navigate]
  );

  const renderFooter = () => {
    if (loadingState === "pagination" && pastBookings?.length > 0) {
      return <div className="spinner"></div>;
    }
    return null;
  };

  const statusHandlerColor = (status) => {
    switch (status) {
      case "0":
      case "7":
        return {
          container: "bg-orange-100 rounded-full py-1 px-4",
          text: "text-xs text-orange-500",
        };
      case "1":
        return {
          container: "bg-brown-100 rounded-full py-1 px-4",
          text: "text-xs text-brown-500",
        };
      case "8":
        return {
          container: "bg-green-100 rounded-full py-1 px-4",
          text: "text-xs text-green-500",
        };
      case "2":
        return {
          container: "bg-blue-100 rounded-full py-1 px-4",
          text: "text-xs text-blue-500",
        };
      case "3":
      case "4":
      case "5":
        return {
          container: "bg-red-100 rounded-full py-1 px-4",
          text: "text-xs text-red-500",
        };
      case "6":
        return {
          container: "bg-blue-100 rounded-full py-1 px-4",
          text: "text-xs text-blue-500",
        };
      case "33":
      case "44":
        return {
          container: "bg-orange-200 rounded-full py-1 px-4",
          text: "text-xs text-orange-600",
        };
      default:
        return { container: "", text: "" };
    }
  };

  const statusHandler = (status) => {
    switch (status) {
      case "0":
        return "For Confirmation";
      case "1":
        return "For Payment Reservation";
      case "2":
        return "Reserved Events";
      case "6":
        return "Successful Event";
      case "3":
        return "Cancelled by you";
      case "4":
        return "Cancelled by Merchant";
      case "5":
        return "Cancelled by Admin";
      case "33":
      case "44":
        return "For Cancellation Approval";
      default:
        return "";
    }
  };

  return (
    <div className="my-4">
      {loadingState === "initial" || isRefreshing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="mb-4 p-4 border border-gray-300 rounded-lg animate-pulse"
            >
              <div className="flex space-x-4">
                <div className="h-24 w-24 rounded-xl bg-gray-200"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 rounded-xl bg-gray-200 w-3/4"></div>
                  <div className="h-3 rounded-xl bg-gray-200 w-1/2"></div>
                  <div className="h-3 rounded-xl bg-gray-200 w-1/2"></div>
                  <div className="h-3 rounded-xl bg-gray-200 w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div>
          {pastBookings.length === 0 ? (
            renderEmpty()
          ) : (
            <div>
              <InfiniteScroll
                dataLength={pastBookings.length}
                next={handleLoadMore}
                hasMore={!isLastPage}
                loader={renderFooter()}
                scrollThreshold={0.5}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-8"
              >
                {pastBookings.map((item) => renderItem(item))}
              </InfiniteScroll>
            </div>
          )}
        </div>
      )}
      <EventDetailsModal
        showIncomeDetails={showIncomeDetails}
        onClose={() =>
          setShowDetails({
            status: false,
            code: "",
            serviceOffered: {},
            isRecommend: false,
          })
        }
        handleRefresh={() => handleRefresh()}
      />
    </div>
  );
};

export default Past;
